/**
 * dockedWidth — how wide the docked chat is, and who is allowed to say so
 * (Miles, 2026-10-08: "user should dynamically always have the ability to
 * change the divider location between the rest of the web application …
 * location remembered per device and tab and latest when user comes back").
 *
 * THE NUMBERS. The pane defaults to 504px — the width it has always had — and
 * a drag holds it between a floor where the footer still fits (380px, the
 * docked width the composer's send-later gate already measures) and a ceiling
 * that leaves the page at least 400px beside it. The stylesheet's width is
 * BUILT from these numbers, so the CSS and the JS twin below cannot drift:
 *
 *   clamp(380px, var(--prest0-docked-width, 504px), max(380px, calc(100vw - 448px)))
 *
 * The browser re-clamps that on every resize with no JS in the loop; a choice
 * made on a large screen survives a trip through a small window untouched.
 *
 * THE MEMORY. "Per device and tab, and the latest when the person comes back":
 * a commit writes the width twice under one name — this tab's own copy in
 * sessionStorage, and the device's latest in localStorage. A read prefers the
 * tab's copy (a reload of this tab keeps this tab's width, whatever another tab
 * chose since), then the device's latest (a new tab, or a person coming back),
 * then the default. A double-click reset removes both.
 *
 * THE VARIABLE. `--prest0-docked-width` is registered as NOT inherited
 * (chatLayoutProperties.ts: an inherited write on an ancestor of the transcript
 * restyles every message — 5-6 s on a long thread). So each element that reads
 * the width holds it itself, through `dockedWidthStyle()` in its own inline
 * style: the pane, the closed right bar, the page beside the rail
 * (App.tsx), the file-preview panel and the sign-in bar. During a drag the
 * divider writes the same variable on the pane and the page only, once a
 * frame; everything else follows on release. Nothing is ever written on <html>.
 *
 * A pure leaf on the first download (scripts/check-chat-first-paint.mjs): the
 * page shell reads the store before the chat engine exists, which is what makes
 * the first frame land at the remembered width with no jump.
 */
import { useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';

/** The custom property every reader holds. */
export const DOCKED_WIDTH_VAR = '--prest0-docked-width';

/** The width the pane has always had, and the default when nobody has chosen. */
export const DOCKED_WIDTH_DEFAULT_PX = 504;
/** The narrowest pane a drag may make: the footer's controls still fit. */
export const DOCKED_WIDTH_MIN_PX = 380;
/** The narrowest the page beside the rail may become. */
export const DOCKED_PAGE_MIN_PX = 400;
/** The pane's own inset from the viewport's right edge (styles.ts `right: 24px`). */
export const DOCKED_PANE_INSET_PX = 24;
/** Clear air between the page and the pane — where the divider sits. */
export const DOCKED_PANE_GUTTER_PX = 24;

/** Viewport pixels the pane can never take: its inset, the gutter, the page's floor. */
const VIEWPORT_RESERVED_PX = DOCKED_PANE_INSET_PX + DOCKED_PANE_GUTTER_PX + DOCKED_PAGE_MIN_PX;

/** The stylesheet's pane width, built from the numbers above. */
export const DOCKED_WIDTH_CSS =
  `clamp(${DOCKED_WIDTH_MIN_PX}px, var(${DOCKED_WIDTH_VAR}, ${DOCKED_WIDTH_DEFAULT_PX}px), `
  + `max(${DOCKED_WIDTH_MIN_PX}px, calc(100vw - ${VIEWPORT_RESERVED_PX}px)))`;

/** One arrow press on the divider. */
export const DOCKED_WIDTH_KEYBOARD_STEP_PX = 16;

/** Where the choice is kept: this tab's copy in sessionStorage, the device's latest in localStorage. */
export const DOCKED_WIDTH_STORAGE_KEY = 'prest0_global_chat_docked_width';

export interface DockedWidthBounds { min: number; max: number }

/** The bounds at a viewport width. The floor wins a fight with the ceiling. */
export function dockedWidthBounds(viewportWidth: number): DockedWidthBounds {
  const vw = Number(viewportWidth) || 0;
  return {
    min: DOCKED_WIDTH_MIN_PX,
    max: Math.max(DOCKED_WIDTH_MIN_PX, vw - VIEWPORT_RESERVED_PX),
  };
}

/**
 * What the stylesheet's clamp() resolves to, in JS. Callers pass
 * `window.innerWidth`, which is what `vw` measures. CSS defines
 * `clamp(min, val, max)` as `max(min, min(val, max))`; this is that expression.
 */
export function dockedPaneWidthPx(viewportWidth: number, choice: number | null): number {
  const { min, max } = dockedWidthBounds(viewportWidth);
  const val = choice !== null && Number.isFinite(choice) ? choice : DOCKED_WIDTH_DEFAULT_PX;
  return Math.max(min, Math.min(val, max));
}

/** A drag's raw width, rounded and held inside the bounds. */
export function clampDockedWidth(px: number, viewportWidth: number): number {
  const { min, max } = dockedWidthBounds(viewportWidth);
  const value = Number(px);
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(Math.round(value), max));
}

/* ── the memory ───────────────────────────────────────────────────────── */

interface StorageLike { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }

function storages(): { tab: StorageLike | null; device: StorageLike | null } {
  if (typeof window === 'undefined') return { tab: null, device: null };
  let tab: StorageLike | null = null;
  let device: StorageLike | null = null;
  try { tab = window.sessionStorage; } catch { tab = null; }
  try { device = window.localStorage; } catch { device = null; }
  return { tab, device };
}

function readFrom(store: StorageLike | null): number | null {
  if (!store) return null;
  try {
    const raw = store.getItem(DOCKED_WIDTH_STORAGE_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw);
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** This tab's choice, else the device's latest, else null (the default serves). */
export function readDockedWidthChoice(): number | null {
  const { tab, device } = storages();
  return readFrom(tab) ?? readFrom(device);
}

function writeChoice(px: number | null): void {
  const { tab, device } = storages();
  for (const store of [tab, device]) {
    if (!store) continue;
    try {
      if (px === null) store.removeItem(DOCKED_WIDTH_STORAGE_KEY);
      else store.setItem(DOCKED_WIDTH_STORAGE_KEY, JSON.stringify(px));
    } catch {
      /* storage refused (private mode, full bin): the width still holds for this page */
    }
  }
}

/* ── the store ────────────────────────────────────────────────────────── */

let choice: number | null | undefined; // undefined ⇒ not read yet
let resizing = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => {
    try { l(); } catch { /* a broken listener must not break the publish loop */ }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The committed choice (null ⇒ the default serves). Read lazily, once per page. */
export function getDockedWidthChoice(): number | null {
  if (choice === undefined) choice = readDockedWidthChoice();
  return choice;
}

/** Record a released drag, a keyboard step or a reset (null), and tell every reader. */
export function commitDockedWidth(px: number | null): void {
  const next = px === null || !Number.isFinite(px) ? null : Math.round(px);
  writeChoice(next);
  if (next === getDockedWidthChoice()) return;
  choice = next;
  notify();
}

/** True between a drag's press and its release — the host suspends its pane-width observer. */
export function getDockedResizing(): boolean {
  return resizing;
}

export function setDockedResizing(next: boolean): void {
  if (next === resizing) return;
  resizing = next;
  notify();
}

/** React: the committed choice; re-renders the caller on a commit, never during a drag. */
export function useDockedWidthChoice(): number | null {
  return useSyncExternalStore(subscribe, getDockedWidthChoice, () => null);
}

/** React: is a divider drag in flight? */
export function useDockedResizing(): boolean {
  return useSyncExternalStore(subscribe, getDockedResizing, () => false);
}

/**
 * The inline style a reader holds: the variable, set on its own element
 * (registered not inherited by chatLayoutProperties.ts — the divider registers
 * before its first write), or nothing when the default serves.
 */
export function dockedWidthStyle(px: number | null): CSSProperties {
  if (px === null) return {};
  return { [DOCKED_WIDTH_VAR]: `${px}px` } as CSSProperties;
}

/** Tests only: forget the store. */
export function resetDockedWidthForTests(): void {
  choice = undefined;
  resizing = false;
  listeners.clear();
}
